/**
 * CustomPaymentsSection Component
 * 
 * Handles visualization and creation of personalized payment types
 * Features:
 * - Display list of custom payment types
 * - Create new custom payment types via modal
 * - Modal follows standard header/footer design
 */

import React, { useState, useEffect } from 'react';
import { Modal, Form, Button } from 'react-bootstrap';
import { toast } from 'react-toastify';
import { IoMdClose, IoMdAdd } from 'react-icons/io';
import { 
  createCustomPaymentType, 
  getCustomPaymentTypesByLocalId 
} from '../../../../../../../../crud/payments_api/payment_types';
import { getLocalId } from '../../utils';
import './CustomPaymentsSection.scss';

interface CustomPaymentType {
  custom_payment_type_id: number;
  local_id: number;
  custom_payment_name: string;
  active: boolean;
  created_at: string;
  updated_at: string;
}

interface CustomPaymentsSectionProps {
  onCustomPaymentsChange?: () => void;
}

const CustomPaymentsSection: React.FC<CustomPaymentsSectionProps> = ({ 
  onCustomPaymentsChange 
}) => {
  const [customPayments, setCustomPayments] = useState<CustomPaymentType[]>([]);
  const [showModal, setShowModal] = useState<boolean>(false);
  const [newPaymentName, setNewPaymentName] = useState<string>('');
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(true);

  // Fetch custom payment types on mount
  useEffect(() => {
    fetchCustomPayments();
  }, []);

  const fetchCustomPayments = async () => {
    try {
      setIsLoading(true);
      const localId = getLocalId();
      const response = await getCustomPaymentTypesByLocalId(localId);
      setCustomPayments(response.data || []);
    } catch (error) {
      console.error('Error fetching custom payment types:', error);
      toast.error('Error al cargar tipos de pago personalizados');
    } finally {
      setIsLoading(false);
    }
  };

  const handleOpenModal = () => {
    setNewPaymentName('');
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setNewPaymentName('');
  };

  const handleCreatePayment = async () => {
    if (!newPaymentName.trim()) {
      toast.error('El nombre del tipo de pago es requerido');
      return;
    }

    try {
      setIsSubmitting(true);
      const localId = getLocalId();
      await createCustomPaymentType(localId, newPaymentName.trim());
      
      toast.success('Tipo de pago personalizado creado exitosamente');
      handleCloseModal();
      
      // Refresh the list
      await fetchCustomPayments();
      
      // Notify parent component if callback provided
      if (onCustomPaymentsChange) {
        onCustomPaymentsChange();
      }
    } catch (error: any) {
      console.error('Error creating custom payment type:', error);
      const errorMessage = error?.response?.data?.message || 'Error al crear el tipo de pago personalizado';
      toast.error(errorMessage);
    } finally {
      setIsSubmitting(false);
    }
  };

  const activePayments = customPayments.filter(p => p.active);

  return (
    <>
      <div className="settings-card custom-payments-section">
        <h4>Tipos de Pago Personalizados</h4>
        
        <div className="custom-payments-content">
          {isLoading ? (
            <p className="loading-text">Cargando...</p>
          ) : activePayments.length === 0 ? (
            <p className="empty-text">No hay tipos de pago personalizados aún</p>
          ) : (
            <div className="custom-payments-list">
              {activePayments.map((payment) => (
                <div key={payment.custom_payment_type_id} className="custom-payment-chip">
                  <span>{payment.custom_payment_name}</span>
                </div>
              ))}
            </div>
          )}
          
          <Button 
            variant="none" 
            className="add-custom-payment-button"
            onClick={handleOpenModal}
          >
            <IoMdAdd size={20} /> Agregar tipo de pago personalizado
          </Button>
        </div>
      </div>

      {/* Creation Modal */}
      <Modal 
        show={showModal} 
        onHide={handleCloseModal}
        centered
        className="custom-payment-modal"
      >
        <Modal.Header>
          <div className="form-modal-title-container">
            <h2 className="form-modal-title">Nuevo Tipo de Pago</h2>
          </div>
          <button 
            className="modal-close-button"
            onClick={handleCloseModal}
            aria-label="Cerrar"
          >
            <IoMdClose size={24} />
          </button>
        </Modal.Header>

        <Modal.Body>
          <Form>
            <Form.Group className="mp-formgroup vertical">
              <Form.Label>Nombre del tipo de pago</Form.Label>
              <Form.Control
                type="text"
                className="modern-input"
                placeholder="Ej: Transferencia Bancaria, Cripto, etc."
                value={newPaymentName}
                onChange={(e) => setNewPaymentName(e.target.value)}
                maxLength={50}
                autoFocus
              />
            </Form.Group>
          </Form>
        </Modal.Body>

        <Modal.Footer>
          <div className="form-modal-footer-custom">
            <Button 
              variant="none" 
              className="btn form-button-cancel"
              onClick={handleCloseModal}
            >
              Cancelar
            </Button>
            <Button 
              variant="none" 
              className="btn form-button-create"
              onClick={handleCreatePayment}
              disabled={isSubmitting || !newPaymentName.trim()}
            >
              {isSubmitting ? 'Creando...' : 'Crear'}
            </Button>
          </div>
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default CustomPaymentsSection;
